import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldCheck, Mail, KeyRound, PenLine, Eraser, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------ Canvas de signature (doigt / souris / import image) ------------ */
export function SignaturePad({ onChange, color = "#0F172A" }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Resize crisp
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.lineWidth = 2.2;
    ctx.strokeStyle = color;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, [color]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches?.[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
    if (empty) setEmpty(false);
  };
  const stop = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    // Notify parent
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onChange?.(empty ? "" : dataUrl);
  };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setEmpty(true);
    onChange?.("");
  };
  const importImage = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        const ctx = c.getContext("2d");
        const rect = c.getBoundingClientRect();
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, rect.width, rect.height);
        // fit contain
        const ratio = Math.min(rect.width / img.width, rect.height / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        ctx.drawImage(img, (rect.width - w) / 2, (rect.height - h) / 2, w, h);
        setEmpty(false);
        onChange?.(c.toDataURL("image/png"));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-md border-2 border-dashed border-slate-300 bg-white overflow-hidden touch-none" style={{ height: 180 }}>
        <canvas
          ref={canvasRef}
          data-testid="signature-pad"
          className="w-full h-full block cursor-crosshair touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center text-slate-400 text-sm">
            <span className="flex items-center gap-2"><PenLine className="h-4 w-4" /> Signez ici avec votre doigt ou votre souris</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={clear} data-testid="signature-clear" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-slate-300 hover:bg-slate-50">
          <Eraser className="h-3.5 w-3.5" /> Effacer
        </button>
        <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-slate-300 hover:bg-slate-50 cursor-pointer">
          <Upload className="h-3.5 w-3.5" /> Importer une image
          <input type="file" accept="image/*" className="hidden" data-testid="signature-upload"
                 onChange={(e)=>importImage(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}

/* ---------------------------- Rendu du corps du doc ---------------------------- */
function renderBody(body) {
  const html = (body || "")
    .split("\n\n").map(p => {
      // gras **texte**
      const withBold = p.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/\n/g, "<br/>");
      return `<p style="margin:0 0 10px 0;line-height:1.55;text-align:justify">${withBold}</p>`;
    }).join("");
  return { __html: html };
}

/* ================================ COMPOSANT PRINCIPAL ================================ */
export default function SignAgentDoc() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", doc: null, company: null });
  const [step, setStep] = useState(1); // 1 email → OTP, 2 OTP, 3 signature
  const [email, setEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [signerRole, setSignerRole] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signedOk, setSignedOk] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [otpResendTimer, setOtpResendTimer] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/agent-docs/sign/${token}`);
        setState({ loading: false, error: "", doc: r.data.doc, company: r.data.company });
        if (r.data.doc?.agent_snapshot?.email) setEmail(r.data.doc.agent_snapshot.email);
      } catch (e) {
        setState({ loading: false, error: e?.response?.data?.detail || "Lien invalide", doc: null, company: null });
      }
    })();
  }, [token]);

  useEffect(() => {
    if (otpResendTimer <= 0) return;
    const t = setTimeout(() => setOtpResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendTimer]);

  const brandColor = state.company?.primary_color || "#0284c7";
  const d = state.doc;
  const c = state.company || {};
  const alreadySigned = d?.status === "signed";

  const requestOtp = async () => {
    setFormErr("");
    if (!email.trim()) return setFormErr("Email obligatoire.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/agent-docs/sign/${token}/request-otp`, { email: email.trim() });
      setOtpResendTimer(30);
      setStep(2);
    } catch (err) {
      setFormErr(err?.response?.data?.detail || "Erreur lors de l'envoi du code.");
    } finally { setSubmitting(false); }
  };

  const verifyOtpLocal = () => {
    setFormErr("");
    if (!otpCode.trim() || otpCode.trim().length < 6) return setFormErr("Merci de saisir le code à 6 chiffres reçu par email.");
    setStep(3);
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    setFormErr("");
    if (!signatureDataUrl) return setFormErr("Merci de dessiner votre signature manuscrite dans le cadre.");
    if (!accepted) return setFormErr("Vous devez cocher « Lu et approuvé » pour signer.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/agent-docs/sign/${token}`, {
        email: email.trim(),
        otp_code: otpCode.trim(),
        signature_image_b64: signatureDataUrl,
        accepted: true,
        signer_role: signerRole.trim(),
      });
      setSignedOk(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur — veuillez réessayer.";
      if (msg.toLowerCase().includes("code")) setStep(2);
      setFormErr(msg);
    } finally { setSubmitting(false); }
  };

  const stepPill = (n, label, IconComp) => {
    const active = step === n;
    const done = step > n;
    const base = "flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors";
    if (done) return <div className={`${base} bg-emerald-100 text-emerald-700`}><CheckCircle2 className="h-3.5 w-3.5" /> {label}</div>;
    if (active) return <div className={`${base} text-white`} style={{ backgroundColor: brandColor }}><IconComp className="h-3.5 w-3.5" /> {label}</div>;
    return <div className={`${base} bg-slate-100 text-slate-500`}><IconComp className="h-3.5 w-3.5" /> {label}</div>;
  };

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement du document…</div>
      </div>
    );
  }
  if (state.error || !state.doc) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md bg-white rounded-md border border-rose-200 p-6 text-center">
          <div className="text-rose-700 font-semibold">Lien invalide</div>
          <div className="text-sm text-slate-600 mt-2">{state.error || "Ce lien de signature est expiré ou inconnu."}</div>
        </div>
      </div>
    );
  }

  const agentName = `${d.agent_snapshot?.first_name || ""} ${d.agent_snapshot?.last_name || ""}`.trim();

  return (
    <div className="min-h-screen bg-slate-100 pb-16" style={{ "--brand-primary": brandColor }} data-testid="sign-agent-doc-page">
      {/* En-tête */}
      <div className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {c.logo_file_id && <img src={`${API}/brand/logo`} alt="logo" className="h-10 object-contain" />}
            <div>
              <div className="font-display font-bold text-lg" style={{ color: brandColor }}>{c.name || "Employeur"}</div>
              <div className="text-[11px] text-slate-500">{c.brand_tagline || "Signature en ligne sécurisée"}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="h-4 w-4" style={{ color: brandColor }} /> Signature avec vérification par email</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {signedOk ? (
          <div className="bg-white rounded-md border border-emerald-200 p-8 text-center shadow-sm">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600 mb-3" />
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Document signé, merci !</h1>
            <p className="text-sm text-slate-600 mt-2">
              Le document <b>{d.title}</b> a bien été signé par <b>{agentName}</b>.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Un email de confirmation, avec le PDF signé complet en pièce jointe, a été envoyé à <b>{email}</b>.
            </p>
          </div>
        ) : (
          <>
            {/* Aperçu document */}
            <div className="bg-white rounded-md border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="font-display text-2xl font-extrabold" style={{ color: brandColor }}>{d.title}</div>
                <div className="text-xs text-slate-500 mt-1">Destinataire : <b>{agentName}</b>{d.agent_snapshot?.email ? ` · ${d.agent_snapshot.email}` : ""}</div>
              </div>
              <div className="px-6 py-5 max-h-[420px] overflow-y-auto prose prose-sm max-w-none text-slate-800" dangerouslySetInnerHTML={renderBody(d.body)} />
            </div>

            {/* Bloc signature */}
            <div className="mt-6 bg-white rounded-md border border-slate-200 p-6 shadow-sm" data-testid="sign-form">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {stepPill(1, "Email", Mail)}
                <div className="h-px w-6 bg-slate-200" />
                {stepPill(2, "Code de vérification", KeyRound)}
                <div className="h-px w-6 bg-slate-200" />
                {stepPill(3, "Signature manuscrite", PenLine)}
              </div>

              {alreadySigned && (
                <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 mb-4">
                  Ce document a déjà été signé le {d.signed_at}. Vous ne pouvez plus le signer à nouveau.
                </div>
              )}

              {/* ÉTAPE 1 : email */}
              {step === 1 && !alreadySigned && (
                <div className="space-y-3">
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Votre email *</div>
                    <input data-testid="sign-email" type="email" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                           value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="agent@exemple.com" />
                    <div className="text-[11px] text-slate-500 mt-1">Utilisez l'email auquel ce document vous a été envoyé.</div>
                  </label>
                  {formErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{formErr}</div>}
                  <button
                    onClick={requestOtp}
                    disabled={submitting || !email.trim()}
                    data-testid="sign-request-otp"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: brandColor }}
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi…</> : <><Mail className="h-4 w-4" /> Recevoir mon code par email</>}
                  </button>
                </div>
              )}

              {/* ÉTAPE 2 : OTP */}
              {step === 2 && !alreadySigned && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700">Un code à 6 chiffres a été envoyé à <b>{email}</b>. Il est valable 15 minutes.</p>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Code de vérification *</div>
                    <input
                      data-testid="sign-otp"
                      inputMode="numeric" maxLength={6}
                      className="w-full max-w-[220px] border border-slate-300 rounded-md px-3 py-2 text-center text-2xl font-mono tracking-[10px]"
                      value={otpCode}
                      onChange={(e)=>setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="••••••"
                    />
                  </label>
                  {formErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{formErr}</div>}
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={verifyOtpLocal} disabled={otpCode.length < 6} data-testid="sign-verify-otp"
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                            style={{ backgroundColor: brandColor }}>
                      <CheckCircle2 className="h-4 w-4" /> Valider le code
                    </button>
                    <button onClick={requestOtp} disabled={otpResendTimer > 0 || submitting} data-testid="sign-resend-otp"
                            className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md text-sm font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-60">
                      {otpResendTimer > 0 ? `Renvoyer (${otpResendTimer}s)` : "Renvoyer le code"}
                    </button>
                    <button onClick={()=>setStep(1)} className="inline-flex items-center gap-1 px-4 py-2.5 rounded-md text-sm text-slate-600 hover:bg-slate-50">← Modifier</button>
                  </div>
                </div>
              )}

              {/* ÉTAPE 3 : signature manuscrite */}
              {step === 3 && !alreadySigned && (
                <form onSubmit={submitSignature} className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                    <div><b>Signataire :</b> {agentName}</div>
                    <div className="text-xs text-slate-600">{email}</div>
                  </div>
                  <label className="block">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Fonction (facultatif)</div>
                    <input data-testid="sign-role" className="w-full max-w-md border border-slate-300 rounded-md px-3 py-2 text-sm"
                           value={signerRole} onChange={(e)=>setSignerRole(e.target.value)} placeholder="Ex : Agent SSIAP 1" />
                  </label>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Votre signature manuscrite *</div>
                    <SignaturePad onChange={setSignatureDataUrl} color={brandColor} />
                    <div className="text-[11px] text-slate-500 mt-1">Signez avec votre doigt sur téléphone/tablette, avec la souris sur ordinateur, ou importez une photo/scan de votre signature.</div>
                  </div>
                  <label className="flex items-start gap-2 text-sm text-slate-700 select-none">
                    <input data-testid="sign-bpa" type="checkbox" className="mt-0.5 h-4 w-4" checked={accepted} onChange={(e)=>setAccepted(e.target.checked)} />
                    <span>
                      <b>Lu et approuvé</b> — Je reconnais avoir pris connaissance du document <b>{d.title}</b> et je l'accepte sans réserve.
                    </span>
                  </label>
                  {formErr && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{formErr}</div>}
                  <button
                    type="submit"
                    data-testid="sign-submit"
                    disabled={submitting || !accepted || !signatureDataUrl}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: brandColor }}
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Signature…</> : <><CheckCircle2 className="h-4 w-4" /> Signer le document</>}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
